/**
 * ╔══════════════════════════════════════════════════════════════════════╗
 * ║  LIFE & WEALTH SOLUTIONS — UNIFIED APP ROUTER                        ║
 * ║  Version: 34.0.0 | 35 Routes | All Pages Routed                    ║
 * ╚══════════════════════════════════════════════════════════════════════╝
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// ── Auth ──────────────────────────────────────────────────────────────────────
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// ── Primary Dashboards ────────────────────────────────────────────────────────
import Dashboard from './pages/Dashboard';
import AnalyticsDashboardEnhanced from './pages/AnalyticsDashboardEnhanced';
import CRMDashboard from './pages/CRMDashboard';

// ── Agent 12 Command Center ───────────────────────────────────────────────────
import Agent12Dashboard from './pages/Agent12Dashboard';

// ── AI & Agent Pages ──────────────────────────────────────────────────────────
import AIAgentControl from './pages/AIAgentControl';
import EnhancedAgentPortal from './pages/EnhancedAgentPortal';
import AgentDashboard from './pages/AgentDashboard';

// ── Predictive & Automation ───────────────────────────────────────────────────
import PredictionsDashboard from './pages/PredictionsDashboard';
import AutomationBuilder from './pages/AutomationBuilder';
import ABTestingDashboard from './pages/ABTestingDashboard';
import MLModelTrainingDashboard from './pages/MLModelTrainingDashboard';
import LeadScoringDashboard from './pages/LeadScoringDashboard';
import ChurnPrevention from './pages/ChurnPrevention';

// ── Notifications ─────────────────────────────────────────────────────────────
import NotificationsCenter from './pages/NotificationsCenter';

// ── CRM Management ────────────────────────────────────────────────────────────
import LeadManagement from './pages/LeadManagement';
import ContactManagement from './pages/ContactManagement';
import DealPipeline from './pages/DealPipeline';
import TaskManagement from './pages/TaskManagement';
import AppointmentScheduler from './pages/AppointmentScheduler';

// ── Business Operations ───────────────────────────────────────────────────────
import CampaignManager from './pages/CampaignManager';
import DocumentManager from './pages/DocumentManager';
import ReferralSystem from './pages/ReferralSystem';
import ComplianceDashboard from './pages/ComplianceDashboard';

// ── Admin & Configuration ─────────────────────────────────────────────────────
import AdminPanel from './pages/AdminPanel';
import IntegrationHub from './pages/IntegrationHub';
import OnboardingWizard from './pages/OnboardingWizard';
import WorkflowBuilder from './components/WorkflowBuilder';
import UserManagement from './pages/UserManagement';
import CallMonitoringDashboard from './pages/CallMonitoringDashboard';
import LegalGateway from './pages/LegalGateway';
import LicenseStatusDashboard from './pages/LicenseStatusDashboard';

// ── PRA Internal — Scraper ────────────────────────────────────────────────────
import ScraperLeadsQueue from './pages/ScraperLeadsQueue';
import ScraperControl from './pages/ScraperControl';

// ── User ──────────────────────────────────────────────────────────────────────
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* ── Public ──────────────────────────────────────────────────────── */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* ── Protected (MainLayout) ──────────────────────────────────────── */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboards */}
          <Route index element={<Dashboard />} />
          <Route path="analytics-enhanced" element={<AnalyticsDashboardEnhanced />} />
          <Route path="crm" element={<CRMDashboard />} />

          {/* Agent 12 */}
          <Route path="agent12" element={<Agent12Dashboard />} />

          {/* AI Agents */}
          <Route path="ai-agents" element={<AIAgentControl />} />
          <Route path="agent-portal" element={<EnhancedAgentPortal />} />
          <Route path="agent-dashboard" element={<AgentDashboard />} />

          {/* Intelligence & Automation */}
          <Route path="predictions" element={<PredictionsDashboard />} />
          <Route path="automation-builder" element={<AutomationBuilder />} />
          <Route path="ab-testing" element={<ABTestingDashboard />} />
          <Route path="ml-training" element={<MLModelTrainingDashboard />} />
          <Route path="lead-scoring" element={<LeadScoringDashboard />} />
          <Route path="churn-prevention" element={<ChurnPrevention />} />
          <Route path="notifications-center" element={<NotificationsCenter />} />

          {/* CRM */}
          <Route path="leads" element={<LeadManagement />} />
          <Route path="contacts" element={<ContactManagement />} />
          <Route path="deals" element={<DealPipeline />} />
          <Route path="tasks" element={<TaskManagement />} />
          <Route path="appointments" element={<AppointmentScheduler />} />

          {/* Operations */}
          <Route path="campaigns" element={<CampaignManager />} />
          <Route path="documents" element={<DocumentManager />} />
          <Route path="referrals" element={<ReferralSystem />} />
          <Route path="compliance" element={<ComplianceDashboard />} />

          {/* Admin */}
          <Route path="admin" element={<AdminPanel />} />
          <Route path="integrations" element={<IntegrationHub />} />
          <Route path="onboarding" element={<OnboardingWizard />} />
          <Route path="workflows" element={<WorkflowBuilder />} />
          <Route path="user-management" element={<UserManagement />} />
          <Route path="call-monitoring" element={<CallMonitoringDashboard />} />
          <Route path="legal-gateway" element={<LegalGateway />} />
          <Route path="license-status" element={<LicenseStatusDashboard />} />

          {/* PRA Internal — Scraper */}
          <Route path="scraper-queue" element={<ScraperLeadsQueue />} />
          <Route path="scraper-control" element={<ScraperControl />} />

          {/* User */}
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* ── 404 ──────────────────────────────────────────────────────────── */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
